import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import {
  Typography,
  FormControlLabel,
  Switch,
  Checkbox,
  Box,
  Grid,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import TransferListContenidos from "./TransferListContenidos";
import { patologias, gruposAlergiasList } from "./constants";
import contenidos from "./contenidos/contenidos.json";
import { GlassPanel, ModernTextField } from "./styledComponents";

const InformacionSalud = forwardRef((props, ref) => {
  const {
    selectedPatologias,
    onPatologiaChange,
    selectedGruposAlergias,
    onGrupoAlergiaChange,
    selectedContenidos,
    onContenidoChange,
    patologiaSearch,
    setPatologiaSearch,
    contenidoSearch,
    setContenidoSearch,
  } = props;

  // Estado para Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Definir categorías con restricciones de selección
  const categoriasRestrictivas = {
    Obesidad: [
      "OBESIDAD_GRADO_1",
      "OBESIDAD_GRADO_2",
      "OBESIDAD_GRADO_3",
      "sobrepeso",
    ],
    Anemia: ["anemia_leve", "anemia_moderada", "anemia_severa", "ANEMIA"],
    Desnutricion: [
      "desnutricion",
      "desnutricion_cronica",
      "desnutricion_leve",
      "desnutricion_moderada",
      "desnutricion_severa",
    ],
  };

  const getCategoria = (clave) => {
    for (const [categoria, claves] of Object.entries(categoriasRestrictivas)) {
      if (claves.includes(clave)) return categoria;
    }
    return null;
  };

  const handlePatologiaChangeWithValidation = (clave, checked) => {
    const categoria = getCategoria(clave);
    if (categoria && checked) {
      const otrasPatologias = categoriasRestrictivas[categoria].filter(
        (p) => p !== clave && selectedPatologias[p]
      );
      if (otrasPatologias.length > 0) {
        setSnackbar({
          open: true,
          message: `Solo puedes seleccionar una patología en la categoría "${categoria}".`,
          severity: "warning",
        });
        return;
      }
    }

    onPatologiaChange({
      ...selectedPatologias,
      [clave]: checked,
    });
  };

  const validate = () => {
    return true;
  };

  const normalizeText = useCallback((text = "") => {
    return text
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();
  }, []);

  useImperativeHandle(ref, () => ({
    validate,
    getData: () => ({
      patologias: selectedPatologias,
      gruposAlergias: selectedGruposAlergias,
      contenidos: selectedContenidos,
    }),
    submit: () => {
      setSnackbar({
        open: true,
        message: "Información de salud guardada exitosamente!",
        severity: "success",
      });
    },
  }));

  const sortedPatologias = useMemo(() => {
    const term = normalizeText(patologiaSearch || "");
    return patologias
      .filter((patologia) => normalizeText(patologia.nombre).includes(term))
      .sort((a, b) => {
        const aSelected = selectedPatologias[a.clave] ? 1 : 0;
        const bSelected = selectedPatologias[b.clave] ? 1 : 0;
        return bSelected - aSelected;
      });
  }, [normalizeText, patologiaSearch, selectedPatologias]);

  const handleGrupoToggle = useCallback(
    (clave, checked) => {
      onGrupoAlergiaChange({
        ...selectedGruposAlergias,
        [clave]: checked,
      });
    },
    [onGrupoAlergiaChange, selectedGruposAlergias]
  );

  return (
    <Box component="section" sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
      <Stack spacing={3}>
        <GlassPanel sx={{ width: "100%" }}>
          <Stack spacing={1.75}>
            <Typography variant="overline" sx={{ letterSpacing: 3 }}>
              Historia clínica
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="h4" fontWeight={800} color="primary.main">
                Información de salud
              </Typography>
              <Typography color="text.secondary">
                Selecciona las patologías, alergias y alimentos que deseas
                evitar para personalizar tu tratamiento y las recomendaciones
                del consultorio.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {["Nutrición funcional", "Preferencias", "Alertas"].map((tag) => (
                <Chip key={tag} label={tag} variant="outlined" size="small" />
              ))}
            </Stack>
          </Stack>
        </GlassPanel>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: { xs: 2, md: 3 },
          }}
        >
          <GlassPanel sx={{ height: "100%", display: "flex" }}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Grupos de alimentos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Indica qué grupos prefieres excluir o a cuáles eres
                  intolerante.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                {gruposAlergiasList.map((grupo) => (
                  <FormControlLabel
                    key={grupo.clave}
                    control={
                      <Switch
                        checked={selectedGruposAlergias[grupo.clave] || false}
                        onChange={(e) =>
                          handleGrupoToggle(grupo.clave, e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: selectedGruposAlergias[grupo.clave]
                            ? 600
                            : 400,
                        }}
                      >
                        {grupo.nombre}
                      </Typography>
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  />
                ))}
              </Box>
            </Stack>
          </GlassPanel>

          <GlassPanel sx={{ height: "100%", display: "flex" }}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Alimentos individuales
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Si un ingrediente no pertenece a un grupo, elimínalo aquí de
                  forma manual.
                </Typography>
              </Box>
              <Divider />
              <TransferListContenidos
                allContenidos={contenidos}
                selectedContenidos={selectedContenidos}
                setSelectedContenidos={onContenidoChange}
                searchTerm={contenidoSearch}
                setSearchTerm={setContenidoSearch}
              />
            </Stack>
          </GlassPanel>
        </Box>

        <GlassPanel sx={{ width: "100%" }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Patologías
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Selecciona las condiciones diagnosticadas por tu profesional de
                salud. Puedes buscar por nombre.
              </Typography>
            </Box>
            <ModernTextField
              placeholder="Buscar patologías..."
              value={patologiaSearch}
              onChange={(e) => setPatologiaSearch(e.target.value)}
              size="small"
            />
            <Divider />
            <Box sx={{ maxHeight: 320, overflow: "auto" }}>
              {sortedPatologias.map((patologia) => (
                <FormControlLabel
                  key={patologia.clave}
                  control={
                    <Checkbox
                      checked={selectedPatologias[patologia.clave] || false}
                      onChange={(e) =>
                        handlePatologiaChangeWithValidation(
                          patologia.clave,
                          e.target.checked
                        )
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: selectedPatologias[patologia.clave]
                          ? 600
                          : 400,
                      }}
                    >
                      {patologia.nombre}
                    </Typography>
                  }
                  sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                />
              ))}
              {sortedPatologias.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 4 }}
                >
                  No se encontraron patologías con ese criterio.
                </Typography>
              )}
            </Box>
          </Stack>
        </GlassPanel>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

InformacionSalud.propTypes = {
  selectedPatologias: PropTypes.object.isRequired,
  onPatologiaChange: PropTypes.func.isRequired,
  selectedGruposAlergias: PropTypes.object.isRequired,
  onGrupoAlergiaChange: PropTypes.func.isRequired,
  selectedContenidos: PropTypes.object.isRequired,
  onContenidoChange: PropTypes.func.isRequired,
  patologiaSearch: PropTypes.string,
  setPatologiaSearch: PropTypes.func,
  contenidoSearch: PropTypes.string,
  setContenidoSearch: PropTypes.func,
};

export default InformacionSalud;
